import { useRef, useState } from 'react';
import type { Task, TaskAttachment } from '../types';
import {
  deleteAttachment as apiDelete,
  uploadAttachment,
} from '../api/attachments';
import { getAttachmentUrl } from '../utils/apiClient';

interface Props {
  task: Task;
  onTaskReplaced: (task: Task) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_BYTES = 25 * 1024 * 1024;

export function TaskAttachments({ task, onTaskReplaced }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const attachments = task.attachments ?? [];

  async function onFileChosen(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    setError(null);
    if (file.size > MAX_BYTES) {
      setError(`File exceeds ${MAX_BYTES / (1024 * 1024)} MB limit`);
      return;
    }
    setUploading(file.name);
    setProgress(0);
    try {
      const updated = await uploadAttachment(task.teamId, task.taskId, file, {
        onProgress: setProgress,
      });
      onTaskReplaced(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(null);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function onDownload(att: TaskAttachment) {
    const key = att.id ?? att.filename;
    setDownloadingId(key);
    try {
      const url = await getAttachmentUrl(task.teamId, task.taskId, key);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloadingId(null);
    }
  }

  async function onRemove(att: TaskAttachment) {
    if (!att.id) return;
    if (!confirm(`Remove "${att.filename}"?`)) return;
    setDeletingId(att.id);
    try {
      const updated = await apiDelete(task.teamId, task.taskId, att.id);
      onTaskReplaced(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section className="task-attachments" aria-label="Attachments">
      <div className="task-attachments-header">
        <span>Attachments{attachments.length > 0 ? ` (${attachments.length})` : ''}</span>
        <button
          type="button"
          className="task-attachments-add"
          onClick={() => fileRef.current?.click()}
          disabled={!!uploading}
        >
          {uploading ? `Uploading ${progress}%…` : '+ Add file'}
        </button>
        <input
          ref={fileRef}
          type="file"
          hidden
          onChange={(e) => void onFileChosen(e.target.files)}
        />
      </div>
      {error && <div className="task-attachments-error">{error}</div>}
      {attachments.length > 0 ? (
        <ul className="task-attachments-list">
          {attachments.map((a) => {
            const key = a.id ?? a.s3Key;
            return (
              <li key={key} className="task-attachments-item">
                <button
                  type="button"
                  className="task-attachments-filename"
                  onClick={() => void onDownload(a)}
                  disabled={downloadingId === (a.id ?? a.filename)}
                  title="Download"
                >
                  📎 {a.filename}
                </button>
                <span className="task-attachments-size">{formatBytes(a.size)}</span>
                {a.id && (
                  <button
                    type="button"
                    className="task-attachments-remove"
                    onClick={() => void onRemove(a)}
                    disabled={deletingId === a.id}
                    aria-label={`Remove ${a.filename}`}
                    title="Remove"
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        !uploading && <div className="task-attachments-empty muted">No files attached.</div>
      )}
    </section>
  );
}
